import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { apiClient } from '@/lib/api';
import { Exam, ExamResult } from '@/lib/types';
import EmptyState from '@/components/ui/EmptyState';
import StartExamModal from '@/components/exams/StartExamModal';

export default function ExamList() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [exams, setExams] = useState<Exam[]>([]);
  const [courseTitles, setCourseTitles] = useState<Record<number, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pendingExam, setPendingExam] = useState<Exam | null>(null);

  useEffect(() => {
    loadExams();
  }, []);

  const loadExams = async () => {
    try {
      setLoading(true);
      const [examsRes, coursesRes] = await Promise.all([
        apiClient.getExams(),
        apiClient.getCourses(),
      ]);

      if (coursesRes.success && coursesRes.data?.results) {
        const map: Record<number, string> = {};
        coursesRes.data.results.forEach((c) => {
          map[c.id] = c.title;
        });
        setCourseTitles(map);
      }

      if (examsRes.success && examsRes.data) {
        setExams(examsRes.data.results || []);
      } else {
        setError(examsRes.error || 'Failed to load exams');
      }
    } catch (err) {
      setError('Failed to load exams');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteExam = async (examId: number) => {
    if (!confirm('Are you sure you want to delete this exam?')) return;

    try {
      const response = await apiClient.deleteExam(examId);
      if (response.success) {
        setExams(exams.filter((exam) => exam.id !== examId));
      } else {
        alert('Failed to delete exam');
      }
    } catch (err) {
      alert('Failed to delete exam');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <p className="text-red-800">{error}</p>
        <button onClick={loadExams} className="mt-2 text-red-600 hover:text-red-800 underline">
          Try again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold text-gray-900">
          {user?.role === 'tutor' ? 'My Exams' : 'Available Exams'}
        </h2>

        {user?.role === 'tutor' && (
          <Link
            to="/exams/create"
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium"
          >
            Create Exam
          </Link>
        )}
      </div>

      {exams.length === 0 ? (
        user?.role === 'tutor' ? (
          <EmptyState
            icon="📝"
            title="You haven't created any exams yet."
            description="Create an exam and attach it to one of your courses."
            action={
              <Link
                to="/exams/create"
                className="inline-block bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-md font-medium"
              >
                Create Your First Exam
              </Link>
            }
            hintsTitle="Getting started:"
            hints={[
              { icon: '❓', label: 'Add questions and options' },
              { icon: '📚', label: 'Assign the exam to a course' },
              { icon: '👨‍🎓', label: 'Enrolled students will see it' },
            ]}
          />
        ) : (
          <EmptyState
            icon="📝"
            title="No exams for your enrolled courses."
            description="Exams appear here when a tutor adds them to a course you are enrolled in."
            action={
              <Link
                to="/courses"
                className="inline-block bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-md font-medium"
              >
                Browse Courses
              </Link>
            }
            hintsTitle="How exams work:"
            hints={[
              { icon: '📚', label: 'Enroll in a course' },
              { icon: '✅', label: 'Complete lessons' },
              { icon: '📝', label: 'Take the exam when it is published' },
            ]}
          />
        )
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {exams.map((exam) => (
            <div key={exam.id} className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{exam.title}</h3>
                <p className="text-gray-600 text-sm mb-4 line-clamp-3">{exam.description}</p>

                <div className="space-y-2 text-sm text-gray-500 mb-4">
                  <p>
                    <strong>Course:</strong>{' '}
                    {courseTitles[exam.course] || `Course #${exam.course}`}
                  </p>
                  <p>
                    <strong>Questions:</strong>{' '}
                    {exam.question_count ?? exam.questions?.length ?? exam.total_marks}
                  </p>
                  <p>
                    <strong>Time:</strong>{' '}
                    {exam.duration_minutes
                      ? `Timed · ${exam.duration_minutes} min`
                      : 'Untimed'}
                  </p>
                  <p>
                    <strong>Created:</strong>{' '}
                    {new Date(exam.created_at).toLocaleDateString()}
                  </p>
                </div>

                {user?.role === 'student' && exam.my_result && (
                  <ExamResultSummary result={exam.my_result} inProgress={Boolean(exam.active_attempt)} />
                )}
                {user?.role === 'student' && !exam.my_result && exam.active_attempt && (
                  <p className="mb-4 text-sm font-medium text-amber-700">In progress</p>
                )}

                <div className="flex space-x-2">
                  {user?.role === 'student' ? (
                    <button
                      type="button"
                      onClick={() => setPendingExam(exam)}
                      className="flex-1 bg-blue-600 hover:bg-blue-700 text-white text-center py-2 px-4 rounded-md text-sm font-medium"
                    >
                      {exam.active_attempt ? 'Continue' : exam.my_result ? 'Retake' : 'Take Exam'}
                    </button>
                  ) : (
                    <Link
                      to={`/exams/${exam.id}`}
                      className="flex-1 bg-blue-600 hover:bg-blue-700 text-white text-center py-2 px-4 rounded-md text-sm font-medium"
                    >
                      View Details
                    </Link>
                  )}

                  {user?.role === 'tutor' && (
                    <>
                      <Link
                        to={`/exams/${exam.id}/edit`}
                        className="bg-yellow-600 hover:bg-yellow-700 text-white py-2 px-4 rounded-md text-sm font-medium"
                      >
                        Edit
                      </Link>
                      <button
                        onClick={() => handleDeleteExam(exam.id)}
                        className="bg-red-600 hover:bg-red-700 text-white py-2 px-4 rounded-md text-sm font-medium"
                      >
                        Delete
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {pendingExam && (
        <StartExamModal
          exam={pendingExam}
          courseTitle={courseTitles[pendingExam.course]}
          onClose={() => setPendingExam(null)}
          onStarted={(id) => {
            setPendingExam(null);
            navigate(`/exams/${id}`);
          }}
        />
      )}
    </div>
  );
}

const GRADE_LABEL: Record<ExamResult['grade'], string> = {
  merit: 'Merit',
  pass: 'Passed',
  fail: 'Not passed',
};

const GRADE_CLASS: Record<ExamResult['grade'], string> = {
  merit: 'bg-emerald-50 text-emerald-800',
  pass: 'bg-blue-50 text-blue-800',
  fail: 'bg-amber-50 text-amber-800',
};

function formatScore(score: ExamResult['score']) {
  const value = Number(score);
  if (Number.isNaN(value)) return String(score);
  return value % 1 === 0 ? `${value.toFixed(0)}%` : `${value.toFixed(1)}%`;
}

function ExamResultSummary({ result, inProgress }: { result: ExamResult; inProgress: boolean }) {
  return (
    <div className="mb-4 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-semibold text-slate-900">{formatScore(result.score)}</span>
        <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${GRADE_CLASS[result.grade]}`}>
          {GRADE_LABEL[result.grade]}
        </span>
        {inProgress && (
          <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800">
            In progress
          </span>
        )}
      </div>
      <p className="mt-1 text-xs text-slate-500">
        {result.attempts > 1 ? `Best of ${result.attempts} attempts` : 'Best score'}
        {' · '}
        {new Date(result.submitted_at).toLocaleDateString(undefined, {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        })}
      </p>
    </div>
  );
}
